import { saveLS, removeLS } from '#helper/localStorage'
import { captureError } from '#helper/sentry'

import checkToast from '#toast'

// Value of the "rear camera (default)" option. It needs no camera list.
export const DEFAULT_CAMERA = 'undefined'

// Sentry diagnostics must not hold back the error report if the browser hangs.
const DIAGNOSTICS_TIMEOUT = 2000

/**
 * Changes the selected camera device and updates localStorage accordingly.
 *
 * @param {String|null} id - The ID of the selected camera device. Use DEFAULT_CAMERA to clear the selection.
 * @returns {Object} - An object containing the updated state properties: { selectedDeviceId, startCam }
 */
export const changeSelected = (id) => {
	if (id === DEFAULT_CAMERA) {
		removeLS('selectedCamera')
	} else {
		saveLS('selectedCamera', id)
	}
	return { selectedDeviceId: id, startCam: false }
}

/**
 * Returns the video constraints for the selected camera.
 * Without a selected camera it asks for the rear camera, so scanning works without a camera list.
 *
 * @param {String|null} id - The ID of the selected camera device, or DEFAULT_CAMERA.
 * @returns {Object} - The constraints: { deviceId } or { facingMode }
 */
export const cameraConstraints = (id) =>
	id && id !== DEFAULT_CAMERA
		? { deviceId: id }
		: { facingMode: 'environment' }

// getUserMedia error names (DOMException.name) and their toast codes.
const CAMERA_ERROR_CODES = {
	NotAllowedError: 16003,
	SecurityError: 16003,
	NotReadableError: 16004,
	TrackStartError: 16004,
	AbortError: 16004,
	NotFoundError: 16005,
	DevicesNotFoundError: 16005,
	OverconstrainedError: 16006,
}

/**
 * Returns the camera permission state without opening the camera.
 *
 * @async
 * @returns {Promise<String>} - 'granted', 'denied', 'prompt' or 'unknown'
 */
const getCameraPermission = async () => {
	try {
		const status = await navigator.permissions.query({ name: 'camera' })
		return status.state
	} catch {
		// Firefox and older Safari can't query "camera".
		return 'unknown'
	}
}

/**
 * Fills `info` with the browser's view of the camera at the time of an error.
 * It writes into `info` step by step, so a timeout still keeps the finished steps.
 *
 * @async
 * @param {Object} info - The object to fill.
 * @returns {Promise<void>}
 */
const collectDiagnostics = async (info) => {
	info.permission = await getCameraPermission()
	try {
		const devices = await navigator.mediaDevices.enumerateDevices()
		info.deviceCounts = devices.reduce(
			(counts, { kind }) => ({
				...counts,
				[kind]: (counts[kind] || 0) + 1,
			}),
			{}
		)
		info.labelledCameras = devices.filter(
			(device) => device.kind === 'videoinput' && device.label
		).length
	} catch (error) {
		info.deviceCounts = `failed: ${error?.name}`
	}
	try {
		// The user agent always says "Android 10; K". This gives the real model and version.
		const device = await navigator.userAgentData.getHighEntropyValues([
			'model',
			'platformVersion',
		])
		info.model = device.model
		info.platform = device.platform
		info.platformVersion = device.platformVersion
	} catch {
		// Only Chromium browsers have userAgentData.
	}
}

/**
 * Sends a camera problem to Sentry, together with the diagnostics.
 *
 * @async
 * @param {Error} error - The error to report.
 * @param {String} step - Where the error happened.
 * @param {Object} [extra] - More data from the caller, e.g. timings.
 * @returns {Promise<void>}
 */
const captureCameraIssue = async (error, step, extra) => {
	const diagnostics = {}
	let timer
	const timedOut = await Promise.race([
		collectDiagnostics(diagnostics).then(() => false),
		new Promise((resolve) => {
			timer = setTimeout(() => resolve(true), DIAGNOSTICS_TIMEOUT)
		}),
	])
	clearTimeout(timer)

	captureError(error, {
		step,
		errorName: error?.name || 'UnknownError',
		errorMessage: error?.message,
		userAgent: navigator.userAgent,
		standalone:
			window.matchMedia?.('(display-mode: standalone)').matches ||
			window.navigator.standalone === true,
		...diagnostics,
		diagnosticsTimedOut: timedOut,
		...extra,
	})
}

/**
 * Shows a toast with the reason a camera could not be opened and reports the error to Sentry.
 * The toast names the browser's error, so the user can send a screenshot.
 *
 * @param {Function} t - The translation function for localization.
 * @param {Error} error - The error from getUserMedia, enumerateDevices or Quagga.
 * @param {String} step - Where the error happened, for Sentry.
 * @param {Object} [extra] - More data for Sentry, e.g. timings.
 * @returns {Promise<void>} - Resolves when the error was sent to Sentry.
 */
export const reportCameraError = (t, error, step, extra) => {
	const name = error?.name || 'UnknownError'
	console.error(`Camera error (${step}): `, error)
	checkToast(t, CAMERA_ERROR_CODES[name] || 16001, undefined, { name })
	return captureCameraIssue(error, step, extra)
}

/**
 * Runs one step and stores how long it took, also when it fails.
 *
 * @async
 * @param {Object} timings - The object that collects the durations in ms.
 * @param {String} key - The name of the step.
 * @param {Function} run - The step.
 * @returns {Promise<*>} - The result of the step.
 */
const timed = async (timings, key, run) => {
	const start = Date.now()
	try {
		return await run()
	} finally {
		timings[key] = Date.now() - start
	}
}

const enumerateCameras = async () => {
	const devices = await navigator.mediaDevices.enumerateDevices()
	return devices.filter((device) => device.kind === 'videoinput')
}

/**
 * Asynchronously retrieves and returns the list of available video input devices.
 * It opens the camera only when the browser needs that to ask for access or to fill in the labels.
 * An empty list is not fatal: the scanner works with DEFAULT_CAMERA.
 *
 * @async
 * @param {Function} t - The translation function for localization.
 * @returns {Promise<Array<Object>>} - A promise that resolves with an array of video input devices.
 */
export const getMediaDevices = async (t) => {
	if (!navigator.mediaDevices?.enumerateDevices) {
		checkToast(t, 16002)
		return []
	}

	const timings = {}
	const extra = { timings, openedCamera: false }
	try {
		extra.permissionBefore = await timed(
			timings,
			'permission',
			getCameraPermission
		)
		const granted = extra.permissionBefore === 'granted'
		let cameras = granted
			? await timed(timings, 'enumerate', enumerateCameras)
			: []

		// Device labels are only filled in after camera access was granted.
		if (!granted || cameras.some((camera) => !camera.label)) {
			extra.openedCamera = true
			const stream = await timed(timings, 'getUserMedia', () =>
				navigator.mediaDevices.getUserMedia({
					video: true,
					audio: false,
				})
			)
			// Close the camera again, otherwise the scanner can't open it later.
			stream.getTracks().forEach((track) => track.stop())
			cameras = await timed(
				timings,
				'enumerateAfterAccess',
				enumerateCameras
			)
		}

		if (cameras.length === 0) {
			// No toast: the selector shows a hint and the default camera still works.
			const error = new Error('The browser listed no cameras')
			error.name = 'CameraListEmpty'
			captureCameraIssue(error, 'getMediaDevices', extra)
		}
		return cameras
	} catch (error) {
		reportCameraError(t, error, 'getMediaDevices', extra)
		return []
	}
}

export default getMediaDevices
